import type { DateRange } from 'react-day-picker';
import { TZDate } from '@date-fns/tz';

import { CALENDAR_MODE, type CalendarMode } from './calendar.types';

/**
 * Resolves the initial month to display based on `defaultMonth`, the current
 * `selected` value (mode-aware), and an optional IANA `timeZone`.
 */
export const getInitialMonth = (
  mode: CalendarMode,
  defaultMonth: Date | undefined,
  selected: Date | DateRange | Date[] | undefined,
  timeZone?: string
): Date => {
  if (defaultMonth) return defaultMonth;
  const today = timeZone ? new TZDate(new Date(), timeZone) : new Date();

  switch (mode) {
    case CALENDAR_MODE.SINGLE:
      return selected instanceof Date ? selected : today;
    case CALENDAR_MODE.RANGE: {
      const range = selected as DateRange | undefined;
      return range?.from ?? today;
    }
    case CALENDAR_MODE.MULTIPLE:
      return Array.isArray(selected) && selected.length > 0 ? selected[0] : today;
  }
};

/* =================================================================
   Date formatters
   Built on Intl.DateTimeFormat via toLocaleDateString — locale-aware out of
   the box. Pass a preset width ('short' | 'medium' | 'long' | 'full') or a
   full Intl options object for fully custom formatting.
   ================================================================= */

/** Preset width that maps to Intl.DateTimeFormat's `dateStyle`. */
export type DateFormatPreset = 'short' | 'medium' | 'long' | 'full';

/** Either a preset shorthand or a full Intl options object. */
export type DateFormat = DateFormatPreset | Intl.DateTimeFormatOptions;

export interface FormatDateOptions {
  /**
   * Preset width or full Intl.DateTimeFormatOptions.
   * - `'short'`  → `4/14/2026` (en-US)
   * - `'medium'` → `Apr 14, 2026`
   * - `'long'`   → `April 14, 2026`
   * - `'full'`   → `Tuesday, April 14, 2026`
   * Pass an options object to compose your own (e.g. `{ year: '2-digit' }`).
   * @default 'short'
   */
  format?: DateFormat;
  /**
   * BCP 47 locale identifier (e.g. `'en-US'`, `'fr-FR'`, `'ja-JP'`).
   * @default 'en-US'
   */
  locale?: string;
  /** IANA time zone (e.g. `'America/New_York'`). */
  timeZone?: string;
}

export interface FormatDateRangeOptions extends FormatDateOptions {
  /**
   * String inserted between `from` and `to`.
   * @default ' – '  (en-dash with spaces)
   */
  separator?: string;
  /**
   * When `from` and `to` fall on the same day, render only one date.
   * @default false
   */
  collapseSameDay?: boolean;
}

export interface FormatDatesOptions extends FormatDateOptions {
  /**
   * String inserted between each formatted date.
   * @default ', '
   */
  separator?: string;
}

const DEFAULT_LOCALE = 'en-US';

const resolveIntlOptions = (
  format: DateFormat,
  timeZone: string | undefined
): Intl.DateTimeFormatOptions => {
  const base: Intl.DateTimeFormatOptions =
    typeof format === 'string' ? { dateStyle: format } : { ...format };
  if (timeZone) base.timeZone = timeZone;
  return base;
};

/**
 * Format a single date.
 *
 * @example
 * ```ts
 * formatDate(new Date(2026, 3, 14));                          // "4/14/2026"
 * formatDate(d, { format: 'medium' });                        // "Apr 14, 2026"
 * formatDate(d, { format: 'long', locale: 'fr-FR' });         // "14 avril 2026"
 * formatDate(d, { format: { year: 'numeric', month: '2-digit', day: '2-digit' } });
 *                                                              // "04/14/2026"
 * ```
 */
export const formatDate = (
  date: Date,
  { format = 'short', locale = DEFAULT_LOCALE, timeZone }: FormatDateOptions = {}
): string => date.toLocaleDateString(locale, resolveIntlOptions(format, timeZone));

/**
 * Format a `DateRange` (`{ from, to }`). Returns an empty string when either
 * end is missing.
 *
 * @example
 * ```ts
 * formatDateRange({ from: a, to: b });
 * // "4/14/2026 – 4/24/2026"
 *
 * formatDateRange(r, { format: 'medium', separator: ' to ' });
 * // "Apr 14, 2026 to Apr 24, 2026"
 *
 * formatDateRange({ from: d, to: d }, { collapseSameDay: true });
 * // "4/14/2026"
 * ```
 */
export const formatDateRange = (
  range: DateRange | undefined,
  options: FormatDateRangeOptions = {}
): string => {
  if (!range?.from || !range?.to) return '';
  const { separator = ' – ', collapseSameDay = false, ...formatOptions } = options;
  const from = formatDate(range.from, formatOptions);
  if (collapseSameDay && range.from.getTime() === range.to.getTime()) return from;
  const to = formatDate(range.to, formatOptions);
  return `${from}${separator}${to}`;
};

/**
 * Format an array of dates as a joined string. Returns an empty string when
 * the array is empty or undefined.
 *
 * @example
 * ```ts
 * formatDates([a, b, c]);                              // "4/14/2026, 4/16/2026, 4/20/2026"
 * formatDates(dates, { format: 'medium', separator: ' • ' });
 * // "Apr 14, 2026 • Apr 16, 2026 • Apr 20, 2026"
 * ```
 */
export const formatDates = (
  dates: Date[] | undefined,
  options: FormatDatesOptions = {}
): string => {
  if (!dates || dates.length === 0) return '';
  const { separator = ', ', ...formatOptions } = options;
  return dates.map((d) => formatDate(d, formatOptions)).join(separator);
};

/**
 * Convert a `Date` to an ISO 8601 date string (`YYYY-MM-DD`) using the
 * date's local timezone. Use `date.toISOString().slice(0, 10)` if you need
 * UTC instead.
 *
 * @example
 * ```ts
 * toISODate(new Date(2026, 3, 14)); // "2026-04-14"
 * ```
 */
export const toISODate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
